import { Fragment, useMemo } from "react";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { ChevronDownIcon, CheckIcon } from "@heroicons/react/24/solid";
import { useLanguage } from "./LanguageContext";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function LanguageSwitcher({
  className = "",
  align = "right",
  compact = false,
  variant = "navbar", // "navbar" or "footer"
}) {
  const { currentLanguage, setLanguage, languages, t } = useLanguage();

  const handleSelectLanguage = (item) => {
    if (currentLanguage.code !== item.code) {
      setLanguage(item.code);
    }
  };

  const isFooter = variant === "footer";

  return (
    <div className={classNames("relative inline-block text-left", className)}>
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <MenuButton
            className={classNames(
              "group inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none",
              isFooter
                ? "rounded-lg bg-white/10 hover:bg-white/20 border border-white/25 hover:border-white/40 text-white px-3 py-1.5 text-xs shadow-sm"
                : compact
                ? "rounded-lg bg-white/10 hover:bg-white/20 border border-white/30 hover:border-white/50 text-white px-2 py-1.5 text-xs shadow-md hover:shadow-lg"
                : "rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-800 shadow-sm hover:border-slate-300 hover:bg-slate-50"
            )}
            title="Change Language"
          >
            <span className="text-sm leading-none mr-1.5" role="img" aria-label={currentLanguage.name}>
              {currentLanguage.flag}
            </span>
            <span className="font-semibold tracking-wide text-xs">
              {currentLanguage.code.toUpperCase()}
            </span>
            <ChevronDownIcon
              className={classNames(
                "ml-1 h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-y-0.5",
                isFooter || compact ? "text-white/80" : "text-slate-500"
              )}
              aria-hidden="true"
            />
          </MenuButton>
        </div>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-100"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems
            className={classNames(
              "absolute z-50 mt-2 w-56 rounded-2xl bg-white p-2 text-slate-800 shadow-2xl ring-1 ring-black/10 focus:outline-none backdrop-blur-md",
              align === "left" ? "left-0 origin-top-left" : "right-0 origin-top-right",
              isFooter ? "bottom-full mb-2 origin-bottom-right" : ""
            )}
          >
            <div className="px-3 py-2 border-b border-slate-100 mb-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {t("common.language", "Select Language")}
              </p>
            </div>

            <div className="max-h-72 overflow-y-auto space-y-0.5 custom-scrollbar">
              {languages.map((item) => {
                const isActive = currentLanguage.code === item.code;

                return (
                  <MenuItem key={item.code}>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={() => handleSelectLanguage(item)}
                        className={classNames(
                          "flex w-full items-center justify-between rounded-xl px-2.5 py-2 text-left transition-all duration-150",
                          focus ? "bg-slate-50" : "",
                          isActive
                            ? "bg-indigo-50/80 text-indigo-900 font-semibold ring-1 ring-indigo-200"
                            : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="text-lg leading-none shrink-0" role="img" aria-label={item.name}>
                            {item.flag}
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-semibold text-slate-900 truncate">
                                {item.nativeName}
                              </span>
                              <span className="text-[10px] uppercase font-bold text-slate-400">
                                {item.code}
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-500 truncate">
                              {item.name}
                            </p>
                          </div>
                        </div>

                        {isActive ? (
                          <CheckIcon
                            className="h-4 w-4 shrink-0 text-indigo-600"
                            aria-hidden="true"
                          />
                        ) : (
                          <span className="h-4 w-4 shrink-0" />
                        )}
                      </button>
                    )}
                  </MenuItem>
                );
              })}
            </div>
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  );
}
